/**
 * Pure parameter curves and gate/retrigger scheduling for the continuous/held-input audio layers
 * (wind, gunfire, fire crackle, stall rush). No `AudioContext` — `engine.ts` calls these every
 * frame and writes the results onto real `AudioParam`s; this file is what makes that math
 * unit-testable without a browser.
 *
 * The **engine** layer is no longer one of them. It is a real OPL2 voice running the game's own
 * bank program 4 on the audio thread; its measured pitch law, RPM lag and level budget live in
 * `engine-voice.ts`. The procedural engine curves below are parked, not live — see the
 * "Procedural rotary-engine synthesis" section.
 *
 * Rebaked (2026-08-19, AdLib SFX lane): `docs/formats/sound-music.md` now confirms ten `SIMSNDS`
 * cues carry SCI's set-loop-point marker and end their scripts with a note still held — real
 * gate-style, continuous material, not the "no loop signal, no dynamics" the previous pass
 * documented here. `loopRegionFor`/`sustainedLoopRegionS`/`decayingLoopRegionS` below turn that
 * into where `engine.ts` should set `AudioBufferSourceNode.loopStart`/`.loopEnd`; `advanceGateRamp`
 * turns a boolean game state (firing, burning, stalled) into a click-free gain ramp for a
 * persistent looped source. Every curve here remains a port-side choice ("modernized, not
 * byte-accurate" per `docs/engineering/audio-plan.md` §3), not a recovered constant — the
 * originals carry no dynamics data for any of this.
 */

export function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

/** Linear ramp from `a` to `b` over `[edge0, edge1]`, clamped outside the range — the shared shape behind every curve below. */
export function rampClamped(value: number, edge0: number, edge1: number, a: number, b: number): number {
  if (edge1 === edge0) return value < edge0 ? a : b;
  const t = clamp01((value - edge0) / (edge1 - edge0));
  return a + (b - a) * t;
}

// --- Loop-region math for the ten SCI loop-marked ("gate-style") SIMSNDS cues, plus wind's own
// non-gate-style-but-still-long-and-natural case. `engine.ts`'s `startLoop` calls `loopRegionFor`
// once per persistent loop and sets the buffer source's `loopStart`/`loopEnd` from the result, so
// the source plays its attack once (start → loopEnd on the first pass) and then repeats only the
// steady region for as long as it stays gated on — never the raw seam of looping the whole,
// decaying buffer from 0.

export interface LoopRegionS {
  readonly startS: number;
  readonly endS: number;
}

/**
 * The fraction of a `sustainedAtEnd` cue's *rendered* length treated as safely steady/held.
 * `renderedSeconds` extends past the cue's own scripted `durationS` only as far as
 * `audio-plan.md` §3's bake rule grants it ("hold the note only until every operator has finished
 * its attack phase") — so the render's own final stretch is, by construction, the part the bake
 * judged worth holding. A quarter of that render is a conservative reading of "final stretch" that
 * holds up across all ten real cues (17 ms cues down to the 3.14 s swell) without per-cue tuning —
 * see `continuous-params.test.ts` for the worked numbers.
 */
export const SUSTAINED_LOOP_TAIL_FRACTION = 0.25;

/**
 * Loop region for a `sustainedAtEnd` cue: the trailing `SUSTAINED_LOOP_TAIL_FRACTION` of the
 * render, but never earlier than `durationS` itself — the scripted note-on span is still inside
 * the instrument's attack trigger, not the held plateau, regardless of how short it is.
 */
export function sustainedLoopRegionS(durationS: number, renderedSeconds: number): LoopRegionS {
  const attackGuardS = Math.max(0, durationS);
  const tailStartS = renderedSeconds * (1 - SUSTAINED_LOOP_TAIL_FRACTION);
  const startS = Math.min(Math.max(attackGuardS, tailStartS), renderedSeconds);
  return { startS, endS: renderedSeconds };
}

/** How much of a naturally-decaying cue's start to skip to clear its attack transient before looping. */
export const DECAYING_LOOP_LEAD_GUARD_S = 0.15;

/**
 * Loop region for a cue that is *not* `sustainedAtEnd` (an ordinary note with its own scripted
 * release, e.g. wind) — skip a short guard at the start (the attack transient) and the release
 * tail at the end (`renderedSeconds` minus the script's own `durationS`, i.e. exactly the extra
 * length the bake's envelope-release measurement added). Falls back to a short region rather than
 * an empty one if the render is too short for the full guard.
 */
export function decayingLoopRegionS(durationS: number, renderedSeconds: number): LoopRegionS {
  const releaseTailS = Math.max(0, renderedSeconds - durationS);
  const leadGuardS = Math.min(DECAYING_LOOP_LEAD_GUARD_S, renderedSeconds / 4);
  const startS = leadGuardS;
  const endS = Math.max(startS + 0.001, renderedSeconds - releaseTailS);
  return { startS, endS };
}

/**
 * Picks the right loop-region rule from a cue's own measured facts (`SfxCueMeta`-shaped). A cue
 * with a measured `loopRegionOverrideS` (e.g. wind's `SIMSNDS-25`, whose RMS envelope was measured
 * offline: silent first second, swell to −13 dB by ~3.5 s, then a long decay — looping the whole
 * body produced a ~10 s loud/fade "breathing" cycle) wins over both generic rules.
 */
export function loopRegionFor(meta: {
  readonly durationS: number;
  readonly renderedSeconds: number;
  readonly sustainedAtEnd: boolean;
  readonly loopRegionOverrideS?: LoopRegionS;
}): LoopRegionS {
  if (meta.loopRegionOverrideS) return meta.loopRegionOverrideS;
  return meta.sustainedAtEnd ? sustainedLoopRegionS(meta.durationS, meta.renderedSeconds) : decayingLoopRegionS(meta.durationS, meta.renderedSeconds);
}

// --- Procedural rotary-engine synthesis — PARKED (2026-08-19, ENGINE SOUND lane).
//
// These curves drove a detuned-sawtooth engine bed built before anyone knew what the original
// actually did. A live OPL-register capture of `RB.EXE` now settles it: the engine is one OPL2
// voice on bank program 4, keyed on once and pitch-swept, with no level modulation, no tremolo
// layer and no cylinder-firing modulator at all (`docs/formats/sound-music.md` § "Continuous
// engine sound", `docs/engineering/audio-plan.md` §6). The port runs that voice for real in an
// `AudioWorklet` — see `engine-voice.ts` / `engine-voice-dsp.ts`.
//
// The circuit and its curves are kept, unwired, behind `engine.ts`'s `ENGINE_SYNTH_ENABLED`
// flag rather than deleted: they are the only piece of this file whose behaviour was tuned
// against a measured mix level (the §3 amendment in `audio-plan.md`), and re-deriving that
// tuning would be the expensive part if a stylised "modern" engine option is ever wanted
// alongside the authentic one. They are NOT the `AudioWorklet` fallback — see
// `engine-worklet-node.ts` for why the fallback is silence instead.

/** Typical WWI rotary layout (Clerget 9B / Bentley BR1) — a 4-stroke cylinder fires once every 2 crank revolutions. */
export const ENGINE_CYLINDER_COUNT = 9;
export const ENGINE_IDLE_RPM = 650;
export const ENGINE_FULL_RPM = 1250;

/** Crank RPM for a given throttle position. */
export function engineRpm(throttle: number): number {
  return rampClamped(throttle, 0, 1, ENGINE_IDLE_RPM, ENGINE_FULL_RPM);
}

/** Cylinder-firing rate in Hz — the AM/tremolo rate that makes the growl read as a multi-cylinder engine rather than one steady buzz. */
export function engineCylinderFireHz(throttle: number): number {
  return (engineRpm(throttle) / 60) * (ENGINE_CYLINDER_COUNT / 2);
}

export const ENGINE_GROWL_HZ_IDLE = 90;
export const ENGINE_GROWL_HZ_FULL = 220;

/** Base pitch of the sawtooth cluster — the engine's perceived exhaust note. */
export function engineGrowlHz(throttle: number): number {
  return rampClamped(throttle, 0, 1, ENGINE_GROWL_HZ_IDLE, ENGINE_GROWL_HZ_FULL);
}

export const ENGINE_FILTER_HZ_IDLE = 400;
export const ENGINE_FILTER_HZ_FULL = 1400;

/** Lowpass cutoff — more high-frequency exhaust crackle audible as RPM rises. */
export function engineFilterHz(throttle: number): number {
  return rampClamped(throttle, 0, 1, ENGINE_FILTER_HZ_IDLE, ENGINE_FILTER_HZ_FULL);
}

/** Matches the baked cue's own measured tremolo character (sound-music.md: AM depth rising 0.20→0.45 over its envelope). */
export const ENGINE_TREMOLO_DEPTH = 0.35;

export const ENGINE_DETUNE_HEALTHY_CENTS = 6;
export const ENGINE_DETUNE_DAMAGED_CENTS = 35;

/** Detune spread across the oscillator cluster widens as the engine is damaged — a "running rough" cue this pipeline can actually render now that the drone is synthesis, not sample playback. */
export function engineDetuneCents(engineHealth: number): number {
  return rampClamped(engineHealth, 0, 1, ENGINE_DETUNE_DAMAGED_CENTS, ENGINE_DETUNE_HEALTHY_CENTS);
}

/**
 * A damaged engine in this sim loses delivered power (see `packages/engine`'s
 * `availableShaftPowerW`), not "runs rough" alone — gain tracks `damage.engine` directly (1 =
 * healthy), fading but never fully silent while the engine exists at all, hence the floor. Also
 * used as the overall engine-bus gain for the procedural synth (`engineDetuneCents` above covers
 * the "rough" timbre; this covers loudness).
 */
export const ENGINE_GAIN_FLOOR = 0.25;

export function engineLoopGain(engineHealth: number): number {
  return ENGINE_GAIN_FLOOR + (1 - ENGINE_GAIN_FLOOR) * clamp01(engineHealth);
}

/**
 * Overall level of the procedural synth in the mix. MEASURED (2026-08-19, offline-render probe —
 * see lessons.md): at 1.0 the sawtooth cluster sat at −10.7 dBFS and dominated the whole
 * soundscape as a constant synthetic drone — 6 dB ABOVE the wind bed's peak, masking every other
 * layer, amplitude-invariant to everything but engine damage (the user's "improper, constantly
 * playing, unmodulated" background). ~−15 dB puts the bed under the one-shots and gated loops.
 */
export const ENGINE_SYNTH_LEVEL = 0.18;

/** At idle throttle the engine is still turning — quiet, not silent. */
export const ENGINE_THROTTLE_GAIN_FLOOR = 0.35;

/**
 * The synth's frame gain: level cap × throttle loudness × health loudness. The pre-fix curve used
 * health alone, so cutting the throttle changed pitch but not volume — measured corr(loudness,
 * throttle) of exactly 0 across a 60 s profile. An engine at constant throttle IS steady, but it
 * must audibly follow the lever.
 */
export function engineOverallGain(throttle: number, engineHealth: number): number {
  const throttleGain = rampClamped(throttle, 0, 1, ENGINE_THROTTLE_GAIN_FLOOR, 1);
  return ENGINE_SYNTH_LEVEL * throttleGain * engineLoopGain(engineHealth);
}

/**
 * True exactly when `active` has just turned on relative to the previous frame — a plain rising-
 * edge detector for a one-shot tied to a state that turns on and off. `engine.ts` uses it for the
 * trigger-pull round of gunfire (the first round fires on the edge; `GUN_SHOT_INTERVAL_S` carries
 * the rest of the burst). Lighter than `advanceGateRamp` for this because a one-shot needs only
 * the edge, not a ramped gain.
 *
 * The other user of this — the `SIMSNDS-00` "engine catches" accent and its Schmitt-trigger
 * hysteresis — is gone: cue 0 was never played in any of the four captured flights, so the
 * original has no engine-start sound to be faithful to (`docs/formats/sound-music.md`
 * § "What this corrects").
 */
export function risingEdge(wasActive: boolean, active: boolean): boolean {
  return active && !wasActive;
}

// --- Wind bed (SIMSNDS-25 — not a loop-marked cue; see its entry in `simsnds-cues.ts`).

/**
 * Retuned 2026-08-19 with the engine-synth level fix: the old 10→55 m/s linear band put the wind
 * at 0.55–0.84 gain across the entire normal speed envelope (35–48 m/s cruise) — near its ceiling
 * at all times, with a perceptual swing of ~4 dB, i.e. a constant bed rather than a speed cue. Now
 * the band spans cruise→dive (a calibrated dive reaches 100 m/s — flight-tunables.md §8.1) and the
 * response is squared, so cruise is subtle (~0.06 at 42 m/s) and a real dive is unmistakable.
 */
export const WIND_AIRSPEED_FLOOR_MPS = 25;
export const WIND_AIRSPEED_CEILING_MPS = 75;
export const WIND_GAIN_MAX = 0.5;

export function windGain(airspeedMps: number): number {
  const t = rampClamped(airspeedMps, WIND_AIRSPEED_FLOOR_MPS, WIND_AIRSPEED_CEILING_MPS, 0, 1);
  return WIND_GAIN_MAX * t * t;
}

export const WIND_RATE_MIN = 0.9;
export const WIND_RATE_MAX = 1.15;

export function windRate(airspeedMps: number): number {
  return rampClamped(airspeedMps, WIND_AIRSPEED_FLOOR_MPS, WIND_AIRSPEED_CEILING_MPS, WIND_RATE_MIN, WIND_RATE_MAX);
}

// --- Gate ramps: a click-free on/off envelope for a persistent looped source, driven by a boolean
// game state (firing / burning / stalled). Symmetric by design — ramping the release the same way
// as the attack means flipping the state never snaps a loop's gain in either direction.

export interface GateRampState {
  /** Seconds the gate has been continuously active (counting up) or inactive (counting back down to 0). */
  readonly forS: number;
  readonly wasActive: boolean;
}

export const GATE_RAMP_IDLE: GateRampState = { forS: 0, wasActive: false };

export interface GateRampResult {
  readonly next: GateRampState;
  /** 0..1, ramped over `rampS` in whichever direction the state just moved. */
  readonly gain: number;
  /** True exactly the frame `active` turns on — for callers that also want to fire a one-shot at that moment (e.g. gunfire's tap cue). */
  readonly risingEdge: boolean;
}

/**
 * Advances one gate-ramp layer by `dtS`. While `active`, `forS` counts up and `gain` ramps 0→1
 * over `rampS`; while inactive it counts back down and `gain` ramps back to 0 over the same
 * window — so a boolean game state can drive a persistent loop's gain without ever jumping it.
 */
export function advanceGateRamp(state: GateRampState, dtS: number, active: boolean, rampS: number): GateRampResult {
  const risingEdge = active && !state.wasActive;
  const dtClamped = Math.max(0, dtS);
  const forS = active ? state.forS + dtClamped : Math.max(0, state.forS - dtClamped);
  const gain = rampClamped(forS, 0, rampS, 0, 1);
  return { next: { forS, wasActive: active }, gain, risingEdge };
}

/**
 * **MEASURED** (2026-08-21, GUN AUDIO lane) — the original's own inter-shot period, read straight
 * off a live OPL2 register capture of `RB.EXE` with the guns firing
 * (`docs/formats/sound-music.md` § "Gunfire, jams and hits").
 *
 * Red Baron does not play a *rattle*. It keys `SIMSNDS` cue 1 on for 101 ms, off for 17 ms, and
 * starts it again — one OPL voice, one key-on **per round**, at a period of 118.0-118.3 ms. That is
 * **8.47 rounds/s ≈ 508 rpm**, the historical Vickers/Spandau rate. The number is identical on the
 * twin-gun Sopwith Camel and the single-gun S.E.5a (AD-011's "rate of fire is per station, a twin
 * mount does not double it", now confirmed at the audio layer), and identical for a tap, a
 * half-second burst and a 25-second hold.
 *
 * This replaces `GUN_HELD_RETRIGGER_S = 0.383` (cue 16's script length), a feel-driven stopgap
 * chosen while no capture of the player's own guns existed.
 */
export const GUN_SHOT_INTERVAL_S = 0.1183;

// --- Dead-trigger clicks: what the original plays when the trigger is down and nothing comes out.

/** One dead-trigger cluster shape — how many clicks it fires and how often the cluster repeats. */
export interface DeadTriggerCluster {
  readonly clicks: number;
  readonly periodS: number;
}

/**
 * MEASURED. Holding the trigger on **jammed** or **empty** guns plays `SIMSNDS` cue 11 in
 * *clusters*: several overlapping 186 ms instances started ~56 ms apart, then a pause, then the
 * cluster again. Mean spacing inside a cluster is 56.3 ms (jammed) / 55.8 ms (empty) — the two
 * states agree — and the individual gaps quantise to 3 or 4 ticks of the SCI player's 60 Hz clock
 * (50.0 / 66.7 ms), so one constant covers both.
 */
export const DEAD_TRIGGER_CLICK_SPACING_S = 0.056;

/** MEASURED cluster shape for a **jam**: 4 clicks per cluster, cluster period 439.5 ms (5 clusters timed, spread 439-440 ms). */
export const JAMMED_TRIGGER_CLUSTER: DeadTriggerCluster = { clicks: 4, periodS: 0.4395 };

/** MEASURED cluster shape for an **empty belt**: 3 clicks per cluster, cluster period 385.8 ms (12 clusters timed, spread 371-406 ms). */
export const EMPTY_TRIGGER_CLUSTER: DeadTriggerCluster = { clicks: 3, periodS: 0.3858 };

/** State for a clustered retrigger layer: elapsed time inside the current cluster, and how many of its clicks have already played. */
export interface ClusterState {
  readonly elapsedS: number;
  readonly firedInCluster: number;
}

export const CLUSTER_IDLE: ClusterState = { elapsedS: 0, firedInCluster: 0 };

export interface ClusterResult {
  readonly next: ClusterState;
  /** True the frame a click should actually be played. */
  readonly fire: boolean;
}

/**
 * Advances one clustered retrigger layer by `dtS`.
 *
 * While `active` the first click fires immediately, the next `clicks - 1` fire at
 * `DEAD_TRIGGER_CLICK_SPACING_S` intervals, and the whole pattern restarts every `periodS` — the
 * "clack-clack-clack-clack … clack-clack-clack-clack" the capture shows for a held trigger on dead
 * guns. Going inactive resets to idle so the next press starts a fresh cluster instead of picking
 * up mid-pattern, which is what the capture shows too: a brief tap on jammed guns produced two
 * clicks and stopped, not a truncated fifth.
 *
 * At most one click is emitted per call. The spacing is longer than a frame at any frame rate above
 * ~18 fps, so a slow frame simply delivers the backlog one click per frame rather than stacking
 * several on the same instant.
 */
export function advanceCluster(state: ClusterState, dtS: number, active: boolean, cluster: DeadTriggerCluster): ClusterResult {
  if (!active) return { next: CLUSTER_IDLE, fire: false };
  const elapsedRaw = state.elapsedS + Math.max(0, dtS);
  const wrapped = elapsedRaw >= cluster.periodS;
  const elapsedS = wrapped ? elapsedRaw % cluster.periodS : elapsedRaw;
  const firedInCluster = wrapped ? 0 : state.firedInCluster;
  const due = firedInCluster < cluster.clicks && elapsedS >= firedInCluster * DEAD_TRIGGER_CLICK_SPACING_S;
  return { next: { elapsedS, firedInCluster: due ? firedInCluster + 1 : firedInCluster }, fire: due };
}

/** Fire-loop bed (`FIRE_LOOP_CUE`, SIMSNDS-19) ramp — a fire catching/dying down is a touch more gradual than a trigger. */
export const FIRE_LOOP_RAMP_S = 0.4;

/** Stall-rush buffet (`STALL_RUSH_LOOP_CUE`, SIMSNDS-24) ramp — buffet builds and settles smoothly, not on/off. */
export const STALL_RUSH_RAMP_S = 0.5;

/** State for a retrigger layer — fire-crackle pops (`FIRE_CRACKLE_POP_CUES`) own one, each independent from the fire-loop bed's gate ramp above. */
export interface RetriggerState {
  /** Seconds since the layer last fired, or since it started being active. */
  readonly accumulatorS: number;
}

export const RETRIGGER_IDLE: RetriggerState = { accumulatorS: 0 };

export interface RetriggerResult {
  readonly next: RetriggerState;
  /** True the frame a one-shot should actually be played. */
  readonly fire: boolean;
}

/**
 * Advances one retrigger layer by `dtS`. While `active`, the accumulator counts up and fires
 * (resetting to the remainder, so a slow frame doesn't lose the excess) every time it crosses
 * `intervalS`; going inactive resets to idle so the next activation starts on a fresh beat rather
 * than picking up mid-interval.
 */
export function advanceRetrigger(state: RetriggerState, dtS: number, active: boolean, intervalS: number): RetriggerResult {
  if (!active) return { next: RETRIGGER_IDLE, fire: false };
  const advanced = state.accumulatorS + Math.max(0, dtS);
  if (advanced < intervalS) return { next: { accumulatorS: advanced }, fire: false };
  return { next: { accumulatorS: advanced % intervalS }, fire: true };
}

/** Fire-crackle pop cadence; `engine.ts` adds jitter on top for an irregular pop pattern. */
export const FIRE_CRACKLE_INTERVAL_S = 0.22;

/** Cycles a fixed pool of cue ids round-robin — `index` is a running play count, not a position. */
export function cycleCue<T>(pool: readonly T[], index: number): T {
  const cue = pool[index % pool.length];
  if (cue === undefined) throw new Error("cycleCue: empty pool");
  return cue;
}

// --- Flak bursts (`sfx-map.ts`'s `FLAK_BURST_CUE`) — a distance-attenuated one-shot per burst
// `combat/flak.ts`'s `stepFlak` reports this frame, keyed off the same `FlakBurst` list the
// visuals draw a sprite from (`flight/encounter-views.ts`'s `effectSpawnsOf`). Unlike the enemy
// engine/gunfire voices (`engine-voice.ts`'s `ENEMY_LEVEL_LADDER`), a burst plays through an
// ordinary baked-buffer one-shot rather than a live OPL2 voice, so the same *shape* of distance
// model — logarithmic falloff between a close floor and an audible ceiling, in metres — comes out
// as a continuous 0..1 gain multiplier on a per-shot `GainNode` instead of a ladder-index register
// write. There is no capture pinning a flak-specific falloff curve (`AA.DAT` gives geometry, not
// audio), so the curve itself is INFERRED — this file's existing enemy-voice model is the only
// precedent, and it is followed exactly rather than invented afresh.

/**
 * INFERRED ceiling: no shell burst farther than this is played at all. Grounded in `AA.DAT`'s own
 * numbers rather than picked freehand — `combat/flak-shell.ts`'s `FLAK_ENVELOPE.rangeM.heavy` (a
 * heavy battery's own engagement radius) is 1905 m, so a burst can occur up to that far from
 * *its target*; rounding up to 2000 m leaves headroom for a burst near a formation-mate a short
 * hop from the player, without extending audibility all the way out to where a battery could not
 * have engaged anything in the first place.
 */
export const FLAK_BURST_AUDIBLE_RANGE_M = 2000;

/** INFERRED floor, matching `ENEMY_CLOSEST_RANGE_M`'s role: full gain at or inside this range, so the log curve never divides by (near-)zero for a burst that goes off right on top of the player. */
export const FLAK_BURST_CLOSEST_RANGE_M = 50;

/**
 * Bursts "come in volleys" (a battery has five independent slots) — capping how many one-shots can
 * be in flight at once keeps a saturated engagement from stacking dozens of overlapping crumps into
 * a wall of noise. Three concurrent one-shots is enough to read as "under fire from more than one
 * gun" without drowning every other bus.
 */
export const FLAK_BURST_MAX_CONCURRENT = 3;

/** INFERRED — same logarithmic range→level shape as `engine-voice.ts`'s `enemyLevelIndexForRange`, returned as a direct 0..1 gain rather than a ladder rung. `0` at/beyond the audible ceiling (silent, and skipped rather than played), `1` at/inside the closest floor. */
export function flakBurstGain(distanceM: number): number {
  if (!Number.isFinite(distanceM) || distanceM > FLAK_BURST_AUDIBLE_RANGE_M) return 0;
  const clamped = Math.max(FLAK_BURST_CLOSEST_RANGE_M, distanceM);
  return Math.log(FLAK_BURST_AUDIBLE_RANGE_M / clamped) / Math.log(FLAK_BURST_AUDIBLE_RANGE_M / FLAK_BURST_CLOSEST_RANGE_M);
}
